/**
 * Theme system types - defined using Zod schemas
 * Types are inferred from schemas (never defined manually)
 */

import { z } from 'zod';

/**
 * Hex color validation pattern
 */
const HexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/).describe('Hex color code (#RRGGBB)');

/**
 * CSS unit value (rem, px, em, etc.)
 */
const CssValueSchema = z.string().min(1).describe('CSS value with units');

export const ButtonRadiusSchema = z
  .string()
  .regex(
    /^(?:0|(?:\d+(?:\.\d+)?|\.\d+)(?:px|rem))$/,
    'buttonRadius must be a non-negative px/rem value',
  )
  .describe('Primary CTA radius emitted as --button-radius');

export type ButtonRadius = z.infer<typeof ButtonRadiusSchema>;

/**
 * Theme colors schema
 */
export const ThemeColorsSchema = z.object({
  primary: HexColorSchema.describe('Primary brand color'),
  primaryDark: HexColorSchema.describe('Dark variant of primary'),
  primaryLight: HexColorSchema.describe('Light variant of primary'),
  accent: HexColorSchema.describe('Accent/secondary color'),
  accentDark: HexColorSchema.describe('Dark variant of accent'),
  accentLight: HexColorSchema.describe('Light variant of accent'),
  background: HexColorSchema.describe('Page background color'),
  backgroundAlt: HexColorSchema.describe('Alternative background'),
  surface: HexColorSchema.describe('Card/surface background'),
  text: HexColorSchema.describe('Primary text color'),
  textMuted: HexColorSchema.describe('Muted/secondary text'),
  textInverse: HexColorSchema.describe('Text on dark backgrounds'),
  border: HexColorSchema.describe('Border color'),
  success: HexColorSchema.describe('Success state color'),
  warning: HexColorSchema.describe('Warning state color'),
  error: HexColorSchema.describe('Error state color'),
  // Medical-specific semantic colors
  evidenceStrong: HexColorSchema.describe('Strong evidence indicator'),
  evidenceModerate: HexColorSchema.describe('Moderate evidence indicator'),
  evidenceLow: HexColorSchema.describe('Low evidence indicator'),
  safetyWarning: HexColorSchema.describe('Safety warning color'),
  safetyInfo: HexColorSchema.describe('Safety info color'),
  // Preset-scoped optional tokens. Emitted as `--color-<kebab>` only when the
  // preset sets them; presets that omit the field render byte-identically to
  // the prior schema (themeToCssVars filters undefined entries).
  goldMid: HexColorSchema.optional().describe(
    'Mid-warm gold accent (cosmetic-atelier divider hairline; brighter than `border`)',
  ),
  // Primary-shade ramp (cosmetic-atelier DS §4 – optional primitive tier).
  // Most presets alias `primaryDark` to their shade-100 stop and never need
  // the deeper plates. The cosmetic-atelier register reaches for shade-300
  // for its near-black dark slabs (TriFeature, BeginWithClarityCTA wide
  // band) so the dark plate retunes alongside the brand primary. Adding
  // these here lets `themeToCssVars` emit `--color-primary-shade-{100..300}`
  // for any preset that declares them, without forcing other presets to
  // populate fields they don't consume.
  primaryShade100: HexColorSchema.optional().describe('Primary shade-100 (deepest semantic dark; usually equal to primaryDark)'),
  primaryShade200: HexColorSchema.optional().describe('Primary shade-200 (between shade-100 and near-black)'),
  primaryShade300: HexColorSchema.optional().describe('Primary shade-300 (near-black plate background)'),
  // Blush pair + inverse-muted tier (HAL-464). Before this, all three were
  // consumed by cosmetic-atelier stylesheets as `var(--token, #hex)` fallbacks
  // that the theme never emitted, so a second provider on the preset inherited
  // Consona's blush pink / rust / cream-gold with no way to configure them.
  blush: HexColorSchema.optional().describe('Blush surface (cosmetic-atelier safety "avoid" card background)'),
  blushRule: HexColorSchema.optional().describe('Blush rule (cosmetic-atelier safety "avoid" card left accent)'),
  textInverseMuted: HexColorSchema.optional().describe('Muted text on dark/inverse surfaces (kickers, arrow glyphs on navy slabs)'),
  // What a photograph's box paints BEFORE the image decodes.
  //
  // WHY this is not just `backgroundAlt`: that token means "the alternating
  // section surface", and image placeholders had been borrowing it. The two
  // jobs are unrelated and they diverge the moment a provider overrides the
  // surface. murrieta-facial-plastic-surgery is the worked example — it sets
  // `backgroundAlt` to a cool green-grey #F2F4EE for its section rhythm, which
  // silently became the colour behind every warm-toned patient photograph on
  // the site and read as a grey box against a bone palette.
  //
  // Consumers MUST fall back to `--color-background-alt` so a preset that
  // omits this renders byte-identically to before (see TreatmentHeroEditorial).
  imagePlaceholder: HexColorSchema.optional().describe(
    'Fill behind a CDN photograph before it decodes; falls back to `backgroundAlt` when unset',
  ),
});

export type ThemeColors = z.infer<typeof ThemeColorsSchema>;

/**
 * Theme typography schema
 *
 * AI-NOTE: `fontSize2xl/3xl/4xl` publish as `--font-size-2xl/3xl/4xl`
 * (hyphen between `size` and the digit). The letter→digit boundary was
 * added to `camelToKebab` in GEN-2028 so the new
 * `colors.primaryShade{100,200,300}` tokens emit with the conventional
 * `--color-primary-shade-100` naming. The typography tokens piggy-backed
 * on that change: pre-GEN-2028 they emitted as `--font-size2xl` (no
 * hyphen) and silently fell through to consumer fallbacks – the
 * `--font-size-2xl` form was always the intended contract.
 */
export const ThemeTypographySchema = z.object({
  fontFamily: z.string().min(1).describe('Base font family stack'),
  fontFamilyHeading: z.string().min(1).describe('Heading font family stack'),
  // Optional serif slots. When a preset sets these, `themeToCss` emits
  // `--font-family-display` / `--font-family-serif` (camelToKebab). Presets
  // that omit them emit nothing extra, so existing presets stay byte-identical.
  // cosmetic-atelier sets all three serif slots to the Cormorant stack so every
  // component fallback chain (`--font-family-heading`, `--font-family-display`,
  // `--font-family-serif`) resolves to ONE self-hosted serif.
  fontFamilyDisplay: z
    .string()
    .min(1)
    .optional()
    .describe('Display/hero font family stack (optional; emits --font-family-display)'),
  fontFamilySerif: z
    .string()
    .min(1)
    .optional()
    .describe('Serif font family stack (optional; emits --font-family-serif)'),
  fontWeightHeading: z.string().min(1).describe('Heading font weight (CSS font-weight value)'),
  fontSizeBase: CssValueSchema.describe('Base font size'),
  fontSizeSm: CssValueSchema.describe('Small font size'),
  fontSizeLg: CssValueSchema.describe('Large font size'),
  fontSizeXl: CssValueSchema.describe('Extra large font size'),
  fontSize2xl: CssValueSchema.describe('2x large font size'),
  fontSize3xl: CssValueSchema.describe('3x large font size'),
  fontSize4xl: CssValueSchema.describe('4x large font size'),
  lineHeightBase: z.string().min(1).describe('Base line height'),
  lineHeightTight: z.string().min(1).describe('Tight line height'),
  lineHeightLoose: z.string().min(1).describe('Loose line height'),
});

export type ThemeTypography = z.infer<typeof ThemeTypographySchema>;

/**
 * Theme spacing schema
 */
export const ThemeSpacingSchema = z.object({
  xs: CssValueSchema.describe('Extra small spacing'),
  sm: CssValueSchema.describe('Small spacing'),
  md: CssValueSchema.describe('Medium spacing'),
  lg: CssValueSchema.describe('Large spacing'),
  xl: CssValueSchema.describe('Extra large spacing'),
  '2xl': CssValueSchema.describe('2x large spacing'),
  '3xl': CssValueSchema.describe('3x large spacing'),
});

export type ThemeSpacing = z.infer<typeof ThemeSpacingSchema>;

/**
 * Theme border radius schema
 */
export const ThemeBorderRadiusSchema = z.object({
  none: CssValueSchema.describe('No border radius'),
  sm: CssValueSchema.describe('Small border radius'),
  md: CssValueSchema.describe('Medium border radius'),
  lg: CssValueSchema.describe('Large border radius'),
  xl: CssValueSchema.describe('Extra large border radius'),
  full: CssValueSchema.describe('Full/pill border radius'),
});

export type ThemeBorderRadius = z.infer<typeof ThemeBorderRadiusSchema>;

/**
 * Theme shadows schema
 */
export const ThemeShadowsSchema = z.object({
  sm: z.string().min(1).describe('Small shadow'),
  md: z.string().min(1).describe('Medium shadow'),
  lg: z.string().min(1).describe('Large shadow'),
  xl: z.string().min(1).describe('Extra large shadow'),
});

export type ThemeShadows = z.infer<typeof ThemeShadowsSchema>;

/**
 * Theme layout schema – website builder preset tokens.
 *
 * These tokens control the visual character of section components across
 * the three presets (clinical, wellness, cosmetic). Optional for backward
 * compatibility with existing entity-page-only themes.
 *
 * AI-NOTE: `themeToCssVars()` maps these fields to CSS custom properties using
 * camelCase-to-kebab-case conversion:
 *   heroStyle       -> --layout-hero-style
 *   cardStyle       -> --layout-card-style
 *   iconStyle       -> --layout-icon-style
 *   buttonStyle     -> --layout-button-style
 *   sectionPadding  -> --layout-section-padding
 *   contentMaxWidth -> --layout-content-max-width
 *
 * Section components read these vars directly (e.g., `var(--layout-section-padding, 4rem)`).
 * The fallback value in each component is the default when layout tokens are absent.
 */
export const ThemeLayoutSchema = z.object({
  heroStyle: z.enum(['gradient', 'image', 'split']).describe(
    'Default hero layout style for this preset. Must match the legacy HeroSectionSchema variant ' +
    'literals (gradient | image | split) – AI-generated YAML uses this as a recommendation when ' +
    'omitting hero.variant. New non-legacy hero variants (e.g. image-fullbleed-mark-centered) ' +
    'are opted into per provider, not via the preset.',
  ),
  cardStyle: z.enum(['border-shadow', 'filled', 'minimal', 'none']).describe(
    'Default card treatment (border+shadow, filled background, minimal border, or no container)',
  ),
  iconStyle: z.enum(['outlined', 'filled', 'geometric']).describe(
    'Default icon treatment for values grids and features',
  ),
  buttonStyle: z.enum(['rounded', 'pill', 'square']).describe(
    'Default button shape: rounded (standard corners), pill (fully rounded), square (sharp edges)',
  ),
  sectionPadding: CssValueSchema.describe('Vertical padding between page sections'),
  contentMaxWidth: CssValueSchema.describe('Max width for prose content (e.g., 65ch)'),
});

export type ThemeLayout = z.infer<typeof ThemeLayoutSchema>;

/**
 * Theme motion schema – cosmetic preset motion tokens.
 *
 * Defined per the Cosmetic Preset Motion Charter (B5,
 * `docs/engineering/2026-04-27-cosmetic-motion-charter.md`).
 *
 * Each token has at least one consuming MR-* rule:
 * - `durationFast`     -> MR-18/19/20/21 micro-interactions, MR-26/27/28 exits
 * - `durationNormal`   -> MR-9 section entrance, MR-26 backdrop, MR-27 paginate
 * - `durationModerate` -> MR-26 modal panel enter, MR-27 lightbox open, MR-28 sheet open
 * - `durationEditorial`-> MR-8 hero entrance choreography (only)
 * - `easeOut`          -> standard fade-up entrances
 * - `easeIn`           -> exit choreography (modal/lightbox/sheet close)
 * - `easeInOut`        -> reserved for symmetric transitions
 * - `easeEditorial`    -> hero + above-the-fold signature-section entrance
 * - `distanceShort`    -> wordmark/headline 8px translate, exit translates
 * - `distanceMedium`   -> 16px section-level translateY on entrance
 * - `distanceLong`     -> 32px desktop sheet fallback (MR-28)
 * - `parallaxMax`      -> hard ceiling on `data-parallax` translate (MR-15)
 *
 * `prefers-reduced-motion: reduce` collapses every motion to instant via the
 * preset's CSS reset (see `packages/themes/src/presets/cosmetic.ts` + Charter
 * MR-2). Components consuming `framer-motion` MUST also call
 * `useReducedMotion()` (MR-3); CSS-only animations are covered by the reset.
 *
 * AI-NOTE: `themeToCssVars()` maps these fields to `--motion-*` CSS variables
 * via camelCase-to-kebab-case conversion. Add new tokens sparingly – the
 * Charter mandates a 5-duration / 4-easing / 3-distance ceiling (§10).
 */
export const ThemeMotionSchema = z.object({
  durationInstant: CssValueSchema.describe('Reduced-motion sentinel (0ms)'),
  durationFast: CssValueSchema.describe('Micro-interaction duration (hover, focus)'),
  durationNormal: CssValueSchema.describe('Standard transition duration (drill-down, tab change)'),
  durationModerate: CssValueSchema.describe('Section-level duration (drawer, sheet, modal)'),
  durationEditorial: CssValueSchema.describe('Hero/signature-section entrance duration (cosmetic register)'),
  easeOut: z.string().min(1).describe('Standard ease-out curve for fade-up entrances'),
  easeIn: z.string().min(1).describe('Ease-in curve for exit choreography'),
  easeInOut: z.string().min(1).describe('Symmetric ease-in-out curve'),
  easeEditorial: z.string().min(1).describe('Long-tailed ease for hero entrances'),
  distanceShort: CssValueSchema.describe('Short translate distance (8px, micro/wordmark/exit)'),
  distanceMedium: CssValueSchema.describe('Medium translate distance (16px, section entrance)'),
  distanceLong: CssValueSchema.describe('Long translate distance (32px, desktop sheet fallback)'),
  parallaxMax: CssValueSchema.describe('Hard ceiling on parallax translate as % of viewport (MR-15)'),
}).strict();

export type ThemeMotion = z.infer<typeof ThemeMotionSchema>;

/**
 * Theme density schema – layout rhythm + reading-measure tokens.
 *
 * Defined to align with `PresetCompositionSchema` rules (C10, the cosmetic
 * preset enforcement gates) and the Visual Charter's homepage section
 * composition rules (HR-10: 4-6 sections; the Performance Budget §1.3).
 *
 * Density tokens describe homepage rhythm; spacing tokens describe component
 * micro-spacing. Keep them separate so layout violations surface as density
 * violations, not as one-off margin tweaks.
 *
 * ## The measure trio (`contentMaxWidth{Display,Lede,Prose}`) — HAL-495
 *
 * These three are deliberately expressed in `ch`, not `rem`/`px`. A pixel
 * measure is the same width for every provider but a DIFFERENT number of
 * characters per line, because each provider ships its own body font: the
 * 768px `max-w-3xl` this replaced measured up to 114 characters in Almarai and
 * fewer in Inter. `ch` (the advance width of "0" in the element's own font at its own
 * size) makes the reading rhythm identical across providers AND across type
 * sizes — the same token on a 24px lede is wider in pixels and identical in
 * characters. That is why the display measure is the SMALLEST number: large
 * type wants fewer characters per line, not more.
 *
 * Consume them through `<Measure>` in `@marketing-optimizer/generators` rather
 * than restating the `var()` at call sites.
 *
 * **Every preset that declares a density block must declare all of these** —
 * the schema is `.strict()`, so a preset that forgets one fails to parse at
 * import time rather than silently falling back to a hardcoded pixel width.
 * That strictness is the point: `contentMaxWidthProse` shipped for months
 * with no consumer, and nothing surfaced the gap.
 */
export const ThemeDensitySchema = z.object({
  homeSectionGap: CssValueSchema.describe('Gap between homepage sections'),
  homeSectionGapDesktop: CssValueSchema.describe('Section gap on desktop (overrides homeSectionGap)'),
  homeMaxSections: z.number().int().min(1).max(12).describe('Max sections allowed on homepage (Charter HR-10)'),
  contentMaxWidthDisplay: CssValueSchema.describe('Display/heading measure in ch (h1, h2) — smallest of the trio; large type wants fewer characters per line'),
  contentMaxWidthLede: CssValueSchema.describe('Lede measure in ch (large intro sentences, pull-quotes, blockquotes)'),
  contentMaxWidthProse: CssValueSchema.describe('Body reading measure in ch (e.g., 68ch) — body copy, FAQ answers, section intros'),
  contentMaxWidthEditorial: CssValueSchema.describe('The page rail: outer max-width shared by header, footer, every section and grid'),
  contentMaxWidthFullBleed: CssValueSchema.describe('Full-bleed (no max-width) sentinel'),
  gutterMobile: CssValueSchema.describe('Outer gutter on mobile'),
  gutterDesktop: CssValueSchema.describe('Outer gutter on desktop'),
}).strict();

export type ThemeDensity = z.infer<typeof ThemeDensitySchema>;

/**
 * Theme hero schema – CSS hooks for full-bleed treatment + mark-on-photo positioning.
 *
 * These tokens are consumed by hero variant components (B1 / C2). The values
 * here are *defaults* for the cosmetic preset. Individual hero variants may
 * override per-variant (e.g. `image-fullbleed-text-bottom-left` shifts the
 * text origin from `center` to `bottom-left`).
 */
export const ThemeHeroSchema = z.object({
  aspectMobile: z.string().min(1).describe('Aspect ratio for mobile hero (e.g., "4 / 5")'),
  aspectDesktop: z.string().min(1).describe('Aspect ratio for desktop hero (e.g., "16 / 9")'),
  markOrigin: z.enum(['top-left', 'top-center', 'top-right', 'center', 'bottom-left', 'bottom-center', 'bottom-right'])
    .describe('Default brand-mark anchor on full-bleed photo hero'),
  textOrigin: z.enum(['top-left', 'top-center', 'top-right', 'center', 'bottom-left', 'bottom-center', 'bottom-right'])
    .describe('Default text anchor on full-bleed photo hero'),
  overlayScrim: z.string().min(1).describe('CSS color/gradient for hero overlay scrim (legibility)'),
    imageFadeDuration: CssValueSchema.describe('LQIP-blur to real-image transition duration'),
}).strict();

export type ThemeHero = z.infer<typeof ThemeHeroSchema>;

/**
 * Font pairing schema – named typography pairings (B2 spec).
 *
 * Each pairing is a self-contained typography slot: display family, body
 * family, optional UI/wordmark family (or null when wordmark inherits the
 * body or display family), font-loading directives, and the woff2 file
 * manifest.
 *
 * The cosmetic-atelier and editorial presets declare one or more named
 * pairings under `<preset>.fontPairings`; providers select via
 * `provider.theme.fontPairing` (key matches `pairings` map). When omitted,
 * the pairing flagged with `default: true` is used.
 *
 * Reference: docs/engineering/2026-04-27-cosmetic-typography-pairing.md (B2,
 * superseded by docs/engineering/2026-05-24-cosmetic-atelier-charter.md).
 */
const FontFaceFileSchema = z.object({
  family: z.string().min(1).describe('CSS font-family value (must match @font-face declaration)'),
  weight: z.string().min(1).describe('CSS font-weight value (e.g., "400", "100 900" for variable fonts)'),
  style: z.enum(['normal', 'italic']).default('normal').describe('CSS font-style value'),
  url: z.string().min(1).describe('Path to the woff2 file (typically /fonts/...)'),
  format: z.enum(['woff2', 'woff2-variations']).describe('CSS font-format hint'),
  preload: z.boolean().default(false).describe('Emit a <link rel="preload"> for this file'),
  display: z.enum(['auto', 'block', 'swap', 'fallback', 'optional']).default('optional')
    .describe('CSS font-display strategy'),
  unicodeRange: z.string().optional().describe('Optional CSS unicode-range subset declaration'),
}).strict();

export type FontFaceFile = z.infer<typeof FontFaceFileSchema>;

export const FontPairingSchema = z.object({
  id: z.string().min(1).describe('Stable kebab-case identifier (e.g., "cormorant-inter")'),
  label: z.string().min(1).describe('Human-readable label for design tooling'),
  default: z.boolean().default(false).describe('Whether this pairing is the preset default'),
  fontFamilyDisplay: z.string().min(1).describe('CSS font-family stack for display headings (Display 1, hero)'),
  fontFamilyHeading: z.string().min(1).describe('CSS font-family stack for headings (H1-H3)'),
  fontFamilyBody: z.string().min(1).describe('CSS font-family stack for body copy'),
  fontFamilyUi: z.string().min(1).describe('CSS font-family stack for tracked-allcaps labels and wordmark'),
  fontWeightDisplay: z.string().min(1).describe('Default display weight'),
  fontWeightHeading: z.string().min(1).describe('Default heading weight'),
  fontWeightBody: z.string().min(1).describe('Default body weight'),
  fontWeightUi: z.string().min(1).describe('Default UI/wordmark weight'),
  trackingDisplay: z.string().min(1).describe('Letter-spacing for display tier (e.g., "-0.02em")'),
  trackingHeading: z.string().min(1).describe('Letter-spacing for heading tier'),
  trackingBody: z.string().min(1).describe('Letter-spacing for body tier'),
  trackingLabel: z.string().min(1).describe('Letter-spacing for tracked-allcaps labels'),
  trackingWordmark: z.string().min(1).describe('Letter-spacing for the wordmark (Charter HR-5: ≥ 0.05em)'),
  // Font-loading directives (B6 performance budget §2)
  files: z.array(FontFaceFileSchema).min(1).describe('woff2 files declared by this pairing'),
}).strict();

export type FontPairing = z.infer<typeof FontPairingSchema>;

/**
 * Color token schema – sRGB hex with optional Display P3 alternate.
 *
 * The discriminated union accepts either a bare hex string (back-compat with
 * existing themes) OR an object with both encodings (`{ srgb, p3 }`).
 *
 * NOT consumed by the live cosmetic preset. The cosmetic palette uses the
 * sibling `ThemeColorsP3Schema` block to pair sRGB tokens with their P3
 * alternates instead of embedding both in a single `WideGamutColor` value.
 * This schema is kept as a stable public API for future presets that prefer
 * the inline-paired form (e.g., when a single token must travel as one
 * unit through a downstream override pipeline).
 *
 * Reference: B6 §4 (`docs/engineering/2026-04-27-cosmetic-performance-budget.md`).
 */
const P3ColorValueSchema = z.string().regex(
  /^color\(display-p3\s+[\d.\s/]+\)$/,
  'Invalid display-p3 color value',
).describe('CSS color() function value with display-p3 color space');

export const WideGamutColorSchema = z.union([
  HexColorSchema,
  z.object({
    srgb: HexColorSchema.describe('sRGB hex fallback'),
    p3: P3ColorValueSchema.describe('Display P3 alternate'),
  }).strict(),
]).describe('Color token: hex string or { srgb, p3 } pair for wide-gamut rendering');

export type WideGamutColor = z.infer<typeof WideGamutColorSchema>;

/**
 * Wide-gamut color overlay schema.
 *
 * Optional `colorsP3` block on a theme declares Display P3 alternates for
 * the existing sRGB color tokens. When present, `themeToCssVars()` emits
 * a paired @supports block so browsers that support color() pick up the
 * P3 values. The base `colors` block remains the source of truth for
 * sRGB rendering and is never replaced.
 */
export const ThemeColorsP3Schema = z.object({
  primary: P3ColorValueSchema.optional(),
  primaryDark: P3ColorValueSchema.optional(),
  primaryLight: P3ColorValueSchema.optional(),
  accent: P3ColorValueSchema.optional(),
  accentDark: P3ColorValueSchema.optional(),
  accentLight: P3ColorValueSchema.optional(),
  background: P3ColorValueSchema.optional(),
  backgroundAlt: P3ColorValueSchema.optional(),
  surface: P3ColorValueSchema.optional(),
  text: P3ColorValueSchema.optional(),
  textMuted: P3ColorValueSchema.optional(),
  textInverse: P3ColorValueSchema.optional(),
  border: P3ColorValueSchema.optional(),
}).strict();

export type ThemeColorsP3 = z.infer<typeof ThemeColorsP3Schema>;

/**
 * Complete theme schema
 */
export const ThemeSchema = z.object({
  name: z.string().min(1).describe('Theme preset name'),
  colors: ThemeColorsSchema,
  colorsP3: ThemeColorsP3Schema.optional().describe('Display P3 alternates for the sRGB color tokens'),
  typography: ThemeTypographySchema,
  spacing: ThemeSpacingSchema,
  borderRadius: ThemeBorderRadiusSchema,
  buttonRadius: ButtonRadiusSchema.optional().describe(
    'Provider-specific primary CTA radius',
  ),
  shadows: ThemeShadowsSchema,
  layout: ThemeLayoutSchema.optional().describe('Layout tokens for website builder sections'),
  motion: ThemeMotionSchema.optional().describe('Motion tokens (durations, easings, distances)'),
  density: ThemeDensitySchema.optional().describe('Density tokens (section gaps, max-widths, gutters)'),
  hero: ThemeHeroSchema.optional().describe('Hero CSS hooks (aspect ratios, mark/text origins, scrim)'),
  fontPairings: z.record(z.string(), FontPairingSchema).optional().describe(
    'Named typography pairings (B2 library). Providers select via provider.theme.fontPairing.',
  ),
});

export type Theme = z.infer<typeof ThemeSchema>;

/**
 * Font overrides for custom typography
 */
export const FontOverridesSchema = z.object({
  body: z.string().optional().describe('Body font family stack (CSS font-family)'),
  heading: z.string().optional().describe('Heading font family stack (CSS font-family)'),
  // Display / serif slots (HAL-464). 21 cosmetic-atelier stylesheets read
  // `--font-family-display` / `--font-family-serif`; before this the only
  // reachable slots were `fontFamily` + `fontFamilyHeading`, so a provider that
  // overrode its fonts still rendered the preset's Cormorant everywhere those
  // two vars are consumed. When omitted, `getTheme()` falls the display + serif
  // slots back to the `heading` override so no dead preset face survives.
  display: z.string().optional().describe('Display/hero font family stack (CSS font-family)'),
  serif: z.string().optional().describe('Serif font family stack (CSS font-family)'),
});

export type FontOverrides = z.infer<typeof FontOverridesSchema>;

/**
 * Extended color overrides for full theme customization.
 *
 * These additional color fields allow providers to fully customize their
 * theme beyond just primary/accent. Primarily used when providers have
 * specific brand colors detected via /detect-design-system.
 */
export const ExtendedColorOverridesSchema = z.object({
  // Core brand colors (auto-generate dark/light variants if not provided)
  primaryColor: HexColorSchema.optional().describe('Override primary color'),
  primaryDarkColor: HexColorSchema.optional().describe('Override primary dark variant'),
  primaryLightColor: HexColorSchema.optional().describe('Override primary light variant'),
  accentColor: HexColorSchema.optional().describe('Override accent color'),
  accentDarkColor: HexColorSchema.optional().describe('Override accent dark variant'),
  accentLightColor: HexColorSchema.optional().describe('Override accent light variant'),
  // Surface colors
  backgroundColor: HexColorSchema.optional().describe('Override page background'),
  backgroundAltColor: HexColorSchema.optional().describe('Override alternate background'),
  surfaceColor: HexColorSchema.optional().describe('Override card/surface background'),
  // Text colors
  textColor: HexColorSchema.optional().describe('Override primary text color'),
  textMutedColor: HexColorSchema.optional().describe('Override muted text color'),
  textInverseColor: HexColorSchema.optional().describe('Override inverse text color'),
  textInverseMutedColor: HexColorSchema.optional().describe('Override muted text on dark/inverse surfaces'),
  // UI colors
  borderColor: HexColorSchema.optional().describe('Override border color'),
  // Preset-scoped tokens (HAL-464). Optional on both sides: presets that
  // don't declare the token emit nothing, and a provider that doesn't
  // override it keeps whatever the preset shipped. Threading them is what
  // makes a preset like cosmetic-atelier reusable for a second customer –
  // these tokens drive the dark slabs, the divider hairline, and the safety
  // card, none of which `primary` / `accent` can reach.
  goldMidColor: HexColorSchema.optional().describe('Override mid-warm gold accent (divider hairline)'),
  primaryShade100Color: HexColorSchema.optional().describe('Override primary shade-100'),
  primaryShade200Color: HexColorSchema.optional().describe('Override primary shade-200'),
  primaryShade300Color: HexColorSchema.optional().describe('Override primary shade-300 (dark slab plate)'),
  blushColor: HexColorSchema.optional().describe('Override blush surface'),
  blushRuleColor: HexColorSchema.optional().describe('Override blush rule/left accent'),
});

export type ExtendedColorOverrides = z.infer<typeof ExtendedColorOverridesSchema>;

/**
 * Theme overrides for runtime customization.
 *
 * RECOMMENDED PATTERN: Providers should use the base theme with color
 * overrides detected from their existing website. The /detect-design-system
 * skill extracts these colors automatically.
 *
 * @example
 * ```typescript
 * // Recommended: base theme with detected colors
 * const theme = getTheme(undefined, {
 *   primaryColor: '#5C4A3D',
 *   accentColor: '#C4A777',
 *   backgroundColor: '#FAF8F5',
 * });
 * ```
 */
export const ThemeOverridesSchema = ExtendedColorOverridesSchema.extend({
  buttonRadius: ButtonRadiusSchema.optional().describe(
    'Override the primary CTA radius without changing component fallbacks',
  ),
  fonts: FontOverridesSchema.optional().describe('Override typography/fonts'),
  fontPairing: z.string().optional().describe(
    'Name of a font pairing declared on the theme (preset.fontPairings[<key>]). ' +
    'When set, selects that pairing instead of the preset default. Ignored if the ' +
    'preset has no fontPairings.',
  ),
});

export type ThemeOverrides = z.infer<typeof ThemeOverridesSchema>;
